/**
 * HistoryView Component
 *
 * Displays all climbing activity grouped by day in reverse chronological order.
 * Days are collapsible and expanded by default.
 * Consecutive attempts for the same route are aggregated into a single row.
 *
 * @example
 * ```tsx
 * <HistoryView />
 * ```
 */

import { useState, useMemo, useCallback, useRef, type CSSProperties } from 'react';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import { useClimbLogs, type ClimbLogWithRoute } from '@/hooks/useClimbLogs';
import { useActiveGym } from '@/hooks/useActiveGym';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { RouteListItem } from '@/components/common/RouteListItem';

/**
 * TODO: Selection/deletion is disabled until UX issues are resolved:
 * - Long-press on desktop flashes and deselects (click event fires after long-press)
 * - Can't deselect aggregated rows
 * - Need to decide how deletion works for partial selections from aggregated rows
 */
const SELECTION_ENABLED = false;

/**
 * Aggregated climb entry - groups consecutive attempts for same route
 */
interface AggregatedClimb {
  /** First log in the group (used for route info) */
  log: ClimbLogWithRoute;
  /** All log IDs in this group (for batch delete), ordered most recent first */
  allLogIds: string[];
  /** Total attempts in this group */
  attemptCount: number;
  /** Whether the group ends with a send */
  hasSend: boolean;
  /** Unique key for React */
  key: string;
}

/**
 * Group of aggregated climbs for a single day
 */
interface DayGroup {
  /** Display label for the day (e.g., "Today", "Yesterday", "January 26, 2026") */
  label: string;
  /** Date key for tracking collapsed state (YYYY-MM-DD format) */
  dateKey: string;
  /** Aggregated climbs for this day */
  climbs: AggregatedClimb[];
}

/**
 * State for inline stepper on aggregated rows
 */
interface InlineStepperState {
  climbKey: string;
  selectCount: number;
}

/**
 * Format a date to a display label
 */
const formatDayLabel = (date: Date): string => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  const dateOnly = new Date(date);
  dateOnly.setHours(0, 0, 0, 0);

  if (dateOnly.getTime() === today.getTime()) {
    return 'Today';
  }

  if (dateOnly.getTime() === yesterday.getTime()) {
    return 'Yesterday';
  }

  return date.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
};

/**
 * Get date key in YYYY-MM-DD format for grouping
 */
const getDateKey = (date: Date): string => {
  return date.toISOString().split('T')[0] ?? '';
};

/**
 * Convert a Firestore timestamp-like value to a Date
 */
const toDate = (value: unknown): Date => {
  if (value instanceof Date) {
    return value;
  }

  if (value && typeof value === 'object' && 'toDate' in value) {
    return (value as { toDate: () => Date }).toDate();
  }

  if (value && typeof value === 'object' && 'seconds' in value) {
    const ts = value as { seconds: number; nanoseconds?: number };
    return new Date(ts.seconds * 1000 + (ts.nanoseconds || 0) / 1000000);
  }

  return new Date();
};

/**
 * Aggregate consecutive attempts for the same route
 */
const aggregateClimbs = (logs: ClimbLogWithRoute[]): AggregatedClimb[] => {
  if (logs.length === 0) return [];

  const aggregated: AggregatedClimb[] = [];
  let currentGroup: ClimbLogWithRoute[] = [];
  let currentRouteId: string | null = null;

  for (const log of logs) {
    const isNewRoute = currentRouteId !== null && log.routeId !== currentRouteId;
    const previousWasSend =
      currentGroup.length > 0 && currentGroup[currentGroup.length - 1]?.outcome === 'send';

    if (isNewRoute || previousWasSend) {
      if (currentGroup.length > 0) {
        const firstLog = currentGroup[0]!;
        aggregated.push({
          log: firstLog,
          allLogIds: currentGroup.map((l) => l.id),
          attemptCount: currentGroup.filter((l) => l.outcome === 'attempt').length,
          hasSend: currentGroup.some((l) => l.outcome === 'send'),
          key: firstLog.id,
        });
      }
      currentGroup = [];
    }

    currentGroup.push(log);
    currentRouteId = log.routeId;
  }

  if (currentGroup.length > 0) {
    const firstLog = currentGroup[0]!;
    aggregated.push({
      log: firstLog,
      allLogIds: currentGroup.map((l) => l.id),
      attemptCount: currentGroup.filter((l) => l.outcome === 'attempt').length,
      hasSend: currentGroup.some((l) => l.outcome === 'send'),
      key: firstLog.id,
    });
  }

  return aggregated;
};

/**
 * Group logs by day in reverse chronological order with aggregation
 */
const groupLogsByDay = (logs: ClimbLogWithRoute[]): DayGroup[] => {
  const groups = new Map<string, ClimbLogWithRoute[]>();

  logs.forEach((log) => {
    const date = toDate(log.loggedAt);
    const dateKey = getDateKey(date);

    if (!groups.has(dateKey)) {
      groups.set(dateKey, []);
    }
    groups.get(dateKey)!.push(log);
  });

  const sortedKeys = Array.from(groups.keys()).sort((a, b) => b.localeCompare(a));

  return sortedKeys.map((dateKey) => {
    const logsForDay = groups.get(dateKey) ?? [];
    const firstLog = logsForDay[0];
    const firstLogDate = firstLog ? toDate(firstLog.loggedAt) : new Date();

    return {
      label: formatDayLabel(firstLogDate),
      dateKey,
      climbs: aggregateClimbs(logsForDay),
    };
  });
};

/**
 * HistoryView displays all climbing activity grouped by day
 */
export function HistoryView() {
  const { logs, loading, deleteClimbLog } = useClimbLogs();
  const { activeGym } = useActiveGym();
  const [collapsedDays, setCollapsedDays] = useState<Set<string>>(new Set());
  const [selectedLogIds, setSelectedLogIds] = useState<Set<string>>(new Set());
  const [inlineStepper, setInlineStepper] = useState<InlineStepperState | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const longPressTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const dayGroups = useMemo(() => groupLogsByDay(logs), [logs]);

  const isSelectionMode = SELECTION_ENABLED && (selectedLogIds.size > 0 || inlineStepper !== null);
  const colorGrades = activeGym?.colorGrades ?? [];
  const selectedLogCount = selectedLogIds.size;

  const toggleDay = (dateKey: string) => {
    setCollapsedDays((prev) => {
      const next = new Set(prev);
      if (next.has(dateKey)) {
        next.delete(dateKey);
      } else {
        next.add(dateKey);
      }
      return next;
    });
  };

  const toggleLogSelection = useCallback((logId: string) => {
    setSelectedLogIds((prev) => {
      const next = new Set(prev);
      if (next.has(logId)) {
        next.delete(logId);
      } else {
        next.add(logId);
      }
      return next;
    });
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedLogIds(new Set());
    setInlineStepper(null);
  }, []);

  const handleLongPressStart = useCallback((logId: string) => {
    if (!SELECTION_ENABLED) return;
    longPressTimerRef.current = setTimeout(() => {
      setSelectedLogIds(new Set([logId]));
    }, 500);
  }, []);

  const handleLongPressEnd = useCallback(() => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  }, []);

  const openInlineStepper = useCallback((climbKey: string) => {
    setInlineStepper({ climbKey, selectCount: 1 });
  }, []);

  const closeInlineStepper = useCallback(() => {
    setInlineStepper(null);
  }, []);

  const adjustSelectCount = useCallback((delta: number, maxCount: number) => {
    setInlineStepper((prev) => {
      if (!prev) return null;
      const newCount = Math.max(1, Math.min(maxCount, prev.selectCount + delta));
      return { ...prev, selectCount: newCount };
    });
  }, []);

  const confirmInlineSelection = useCallback((allLogIds: string[], selectCount: number) => {
    // Add the selected logs to the selection set
    const idsToSelect = allLogIds.slice(0, selectCount);
    setSelectedLogIds((prev) => {
      const next = new Set(prev);
      idsToSelect.forEach((id) => next.add(id));
      return next;
    });
    setInlineStepper(null);
  }, []);

  const handleDeleteSelected = useCallback(async () => {
    if (isDeleting || selectedLogIds.size === 0) return;
    setIsDeleting(true);

    try {
      for (const logId of selectedLogIds) {
        await deleteClimbLog(logId);
      }
      clearSelection();
    } catch (error) {
      console.error('Failed to delete climb log(s):', error);
    } finally {
      setIsDeleting(false);
    }
  }, [deleteClimbLog, selectedLogIds, clearSelection, isDeleting]);

  if (loading) {
    return <LoadingSpinner />;
  }

  if (logs.length === 0) {
    return (
      <div style={containerStyle}>
        <p style={emptyTextStyle}>No climbs logged yet. Start climbing!</p>
      </div>
    );
  }

  return (
    <div style={containerStyle}>
      {dayGroups.map((group) => {
        const isCollapsed = collapsedDays.has(group.dateKey);

        return (
          <div key={group.dateKey} style={dayGroupStyle}>
            <button
              type="button"
              style={dayHeaderStyle}
              onClick={() => toggleDay(group.dateKey)}
              aria-expanded={!isCollapsed}
            >
              <span style={dayLabelStyle}>{group.label}</span>
              {isCollapsed ? (
                <ExpandMoreIcon style={chevronIconStyle} />
              ) : (
                <ExpandLessIcon style={chevronIconStyle} />
              )}
            </button>

            {!isCollapsed && (
              <div style={logsContainerStyle}>
                {group.climbs.map((climb) => {
                  const isStepperOpen = inlineStepper?.climbKey === climb.key;
                  const selectedInRow = climb.allLogIds.filter((id) =>
                    selectedLogIds.has(id)
                  ).length;

                  return (
                    <ClimbRow
                      key={climb.key}
                      climb={climb}
                      colorGrades={colorGrades}
                      isSelected={selectedLogIds.has(climb.log.id)}
                      selectedInRow={selectedInRow}
                      isSelectionMode={isSelectionMode}
                      isStepperOpen={isStepperOpen}
                      stepperCount={isStepperOpen ? inlineStepper.selectCount : 0}
                      onToggleSelection={() => toggleLogSelection(climb.log.id)}
                      onOpenStepper={() => openInlineStepper(climb.key)}
                      onCloseStepper={closeInlineStepper}
                      onAdjustCount={(delta) => adjustSelectCount(delta, climb.allLogIds.length)}
                      onConfirmSelection={() =>
                        confirmInlineSelection(climb.allLogIds, inlineStepper?.selectCount ?? 1)
                      }
                      onLongPressStart={() => handleLongPressStart(climb.log.id)}
                      onLongPressEnd={handleLongPressEnd}
                    />
                  );
                })}
              </div>
            )}
          </div>
        );
      })}

      {/* Selection Mode Toolbar */}
      {isSelectionMode && selectedLogCount > 0 && (
        <div style={selectionToolbarStyle}>
          <button
            type="button"
            style={cancelButtonStyle}
            onClick={clearSelection}
            aria-label="Cancel selection"
          >
            <CloseIcon style={toolbarIconStyle} />
          </button>
          <span style={selectionCountStyle}>{selectedLogCount} selected</span>
          <button
            type="button"
            style={deleteSelectedButtonStyle}
            onClick={handleDeleteSelected}
            disabled={isDeleting}
            aria-label={`Delete ${selectedLogCount} logs`}
          >
            <DeleteOutlineIcon style={toolbarIconStyle} />
            <span>{isDeleting ? 'Deleting...' : 'Delete'}</span>
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * Format a timestamp for display
 */
const formatTimestamp = (value: unknown): string => {
  const date = toDate(value);
  return date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
};

/**
 * ClimbRow displays a climb entry with selection support
 */
function ClimbRow({
  climb,
  colorGrades,
  isSelected,
  selectedInRow,
  isSelectionMode,
  isStepperOpen,
  stepperCount,
  onToggleSelection,
  onOpenStepper,
  onCloseStepper,
  onAdjustCount,
  onConfirmSelection,
  onLongPressStart,
  onLongPressEnd,
}: {
  climb: AggregatedClimb;
  colorGrades: { displayName: string; color: string }[];
  isSelected: boolean;
  selectedInRow: number;
  isSelectionMode: boolean;
  isStepperOpen: boolean;
  stepperCount: number;
  onToggleSelection: () => void;
  onOpenStepper: () => void;
  onCloseStepper: () => void;
  onAdjustCount: (delta: number) => void;
  onConfirmSelection: () => void;
  onLongPressStart: () => void;
  onLongPressEnd: () => void;
}) {
  const { log, allLogIds, attemptCount, hasSend } = climb;
  const routeData = log.routeDisplayData;
  const totalLogs = allLogIds.length;
  const isAggregated = totalLogs > 1;

  let outcomeText: string;
  if (hasSend && attemptCount > 0) {
    outcomeText = `${attemptCount} att → Send`;
  } else if (hasSend) {
    outcomeText = 'Send';
  } else if (attemptCount > 1) {
    outcomeText = `${attemptCount} attempts`;
  } else {
    outcomeText = 'Attempt';
  }

  const timestamp = formatTimestamp(log.loggedAt);

  const handleRowClick = useCallback(() => {
    if (isStepperOpen) return; // Don't toggle if stepper is open
    if (isSelectionMode) {
      if (isAggregated) {
        onOpenStepper();
      } else {
        onToggleSelection();
      }
    }
  }, [isSelectionMode, isAggregated, isStepperOpen, onOpenStepper, onToggleSelection]);

  const handleTouchStart = useCallback(() => {
    if (!isSelectionMode && !isStepperOpen) {
      onLongPressStart();
    }
  }, [isSelectionMode, isStepperOpen, onLongPressStart]);

  const handleTouchEnd = useCallback(() => {
    onLongPressEnd();
  }, [onLongPressEnd]);

  const handleMouseDown = useCallback(() => {
    if (!isSelectionMode && !isStepperOpen) {
      onLongPressStart();
    }
  }, [isSelectionMode, isStepperOpen, onLongPressStart]);

  const handleMouseUp = useCallback(() => {
    onLongPressEnd();
  }, [onLongPressEnd]);

  const handleMouseLeave = useCallback(() => {
    onLongPressEnd();
  }, [onLongPressEnd]);

  const hasSelectedInRow = selectedInRow > 0;

  return (
    <div style={climbRowWrapperStyle}>
      <div
        style={{
          ...climbRowContainerStyle,
          ...(isSelected ? selectedRowStyle : {}),
          ...(isAggregated && hasSelectedInRow ? partialSelectedRowStyle : {}),
        }}
        onClick={handleRowClick}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseLeave}
        role="button"
        tabIndex={0}
        aria-selected={isSelected}
      >
        {/* Selection indicator */}
        {isSelectionMode && (
          <div style={checkboxContainerStyle}>
            {isAggregated ? (
              <span style={hasSelectedInRow ? aggregatedBadgeSelectedStyle : aggregatedBadgeStyle}>
                {hasSelectedInRow ? selectedInRow : totalLogs}
              </span>
            ) : isSelected ? (
              <CheckBoxIcon style={checkboxSelectedStyle} />
            ) : (
              <CheckBoxOutlineBlankIcon style={checkboxStyle} />
            )}
          </div>
        )}

        <RouteListItem
          name={routeData?.name ?? ''}
          wallSection={routeData?.wallSection ?? log.routeName ?? 'Unknown Route'}
          officialGrade={routeData?.officialGrade ?? null}
          colorGrades={colorGrades}
          ariaLabel={`${outcomeText}${totalLogs > 1 ? ` (${totalLogs} logs)` : ''} - ${timestamp}`}
          {...(routeData?.colorGrade && { colorGrade: routeData.colorGrade })}
          {...(routeData?.identification && { identification: routeData.identification })}
        >
          <div style={rowRightContentStyle}>
            <span style={timestampStyle}>{timestamp}</span>
            <span style={outcomeStyle}>{outcomeText}</span>
            {hasSend && <CheckCircleIcon style={checkIconStyle} />}
          </div>
        </RouteListItem>
      </div>

      {/* Inline stepper for aggregated rows */}
      {isStepperOpen && (
        <div style={inlineStepperStyle}>
          <span style={inlineStepperLabelStyle}>Select how many?</span>
          <div style={inlineStepperControlsStyle}>
            <button
              type="button"
              style={inlineStepperButtonStyle}
              onClick={() => onAdjustCount(-1)}
              disabled={stepperCount <= 1}
              aria-label="Decrease"
            >
              <RemoveIcon style={inlineStepperIconStyle} />
            </button>
            <span style={inlineStepperValueStyle}>{stepperCount}</span>
            <button
              type="button"
              style={inlineStepperButtonStyle}
              onClick={() => onAdjustCount(1)}
              disabled={stepperCount >= totalLogs}
              aria-label="Increase"
            >
              <AddIcon style={inlineStepperIconStyle} />
            </button>
          </div>
          <div style={inlineStepperActionsStyle}>
            <button type="button" style={inlineStepperCancelStyle} onClick={onCloseStepper}>
              Cancel
            </button>
            <button type="button" style={inlineStepperConfirmStyle} onClick={onConfirmSelection}>
              Select {stepperCount}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// Styles

const containerStyle: CSSProperties = {
  padding: 'var(--spacing-md)',
};

const emptyTextStyle: CSSProperties = {
  color: 'var(--color-text-secondary)',
  textAlign: 'center',
  padding: 'var(--spacing-lg)',
};

const dayGroupStyle: CSSProperties = {
  marginBottom: 'var(--spacing-md)',
};

const dayHeaderStyle: CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  width: '100%',
  padding: 'var(--spacing-sm) var(--spacing-md)',
  backgroundColor: 'var(--color-bg-secondary)',
  border: 'none',
  borderRadius: 'var(--radius-md)',
  cursor: 'pointer',
  textAlign: 'left',
};

const dayLabelStyle: CSSProperties = {
  fontSize: 'var(--font-size-base)',
  fontWeight: 600,
  color: 'var(--color-text-primary)',
};

const chevronIconStyle: CSSProperties = {
  fontSize: '20px',
  color: 'var(--color-text-secondary)',
};

const logsContainerStyle: CSSProperties = {
  marginTop: 'var(--spacing-xs)',
};

const climbRowWrapperStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
};

const climbRowContainerStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  cursor: 'pointer',
  userSelect: 'none',
  WebkitUserSelect: 'none',
};

const selectedRowStyle: CSSProperties = {
  backgroundColor: 'var(--color-primary-light, #eff6ff)',
};

const partialSelectedRowStyle: CSSProperties = {
  backgroundColor: 'var(--color-primary-light, #f0f9ff)',
};

const checkboxContainerStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '40px',
  flexShrink: 0,
  paddingLeft: '12px',
};

const checkboxStyle: CSSProperties = {
  fontSize: '24px',
  color: 'var(--color-text-secondary, #9ca3af)',
};

const checkboxSelectedStyle: CSSProperties = {
  fontSize: '24px',
  color: 'var(--color-primary, #3b82f6)',
};

const aggregatedBadgeStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '24px',
  height: '24px',
  borderRadius: '12px',
  backgroundColor: 'var(--color-primary, #3b82f6)',
  color: '#ffffff',
  fontSize: '0.75rem',
  fontWeight: 600,
};

const aggregatedBadgeSelectedStyle: CSSProperties = {
  ...aggregatedBadgeStyle,
  backgroundColor: '#ef4444',
};

const rowRightContentStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
};

const timestampStyle: CSSProperties = {
  fontSize: '0.7rem',
  color: 'var(--color-text-tertiary, #9ca3af)',
  whiteSpace: 'nowrap',
};

const outcomeStyle: CSSProperties = {
  fontSize: '0.75rem',
  color: 'var(--color-text-secondary)',
  whiteSpace: 'nowrap',
};

const checkIconStyle: CSSProperties = {
  fontSize: '20px',
  color: '#22c55e',
};

// Selection toolbar styles
const selectionToolbarStyle: CSSProperties = {
  position: 'fixed',
  bottom: '80px',
  left: '16px',
  right: '16px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '12px 16px',
  backgroundColor: 'var(--color-bg-primary, #ffffff)',
  borderRadius: 'var(--radius-lg, 12px)',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
  zIndex: 100,
};

const cancelButtonStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '40px',
  height: '40px',
  backgroundColor: 'transparent',
  border: 'none',
  borderRadius: '50%',
  cursor: 'pointer',
  color: 'var(--color-text-secondary, #6b7280)',
};

const selectionCountStyle: CSSProperties = {
  fontSize: '0.875rem',
  fontWeight: 500,
  color: 'var(--color-text-primary, #1f2937)',
};

const deleteSelectedButtonStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  padding: '10px 20px',
  backgroundColor: '#ef4444',
  border: 'none',
  borderRadius: 'var(--radius-md, 8px)',
  cursor: 'pointer',
  fontSize: '0.875rem',
  fontWeight: 600,
  color: '#ffffff',
};

const toolbarIconStyle: CSSProperties = {
  fontSize: '20px',
};

// Inline stepper styles
const inlineStepperStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '12px 16px',
  backgroundColor: 'var(--color-bg-secondary, #f9fafb)',
  borderBottom: '1px solid var(--color-border, #e5e7eb)',
};

const inlineStepperLabelStyle: CSSProperties = {
  fontSize: '0.8rem',
  color: 'var(--color-text-secondary, #6b7280)',
};

const inlineStepperControlsStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
};

const inlineStepperButtonStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '32px',
  height: '32px',
  borderRadius: '16px',
  backgroundColor: 'var(--color-bg-primary, #ffffff)',
  border: '1px solid var(--color-border, #e5e7eb)',
  cursor: 'pointer',
  color: 'var(--color-text-primary, #1f2937)',
};

const inlineStepperIconStyle: CSSProperties = {
  fontSize: '18px',
};

const inlineStepperValueStyle: CSSProperties = {
  fontSize: '1rem',
  fontWeight: 600,
  color: 'var(--color-text-primary, #1f2937)',
  minWidth: '32px',
  textAlign: 'center',
};

const inlineStepperActionsStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
};

const inlineStepperCancelStyle: CSSProperties = {
  padding: '6px 12px',
  borderRadius: 'var(--radius-sm, 6px)',
  border: '1px solid var(--color-border, #e5e7eb)',
  backgroundColor: 'transparent',
  cursor: 'pointer',
  fontSize: '0.75rem',
  fontWeight: 500,
  color: 'var(--color-text-secondary, #6b7280)',
};

const inlineStepperConfirmStyle: CSSProperties = {
  padding: '6px 12px',
  borderRadius: 'var(--radius-sm, 6px)',
  border: 'none',
  backgroundColor: 'var(--color-primary, #3b82f6)',
  cursor: 'pointer',
  fontSize: '0.75rem',
  fontWeight: 600,
  color: '#ffffff',
};

export default HistoryView;
